import React from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useMatch, useMatches, useParams } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { useActiveOrganization } from "@/contexts"
import { SitePath } from "@/models/route"
import type { SiteLayoutContext } from "@/modules/site/context/site-layout-context"
import { useGetSite } from "@/services/site/use-get-site"
import { useToggle } from "@/services/toggle"
import { resolvePageLayoutMode } from "@/utils/page-layout-mode"
import { useUrl } from "@/utils/url"

import { SiteInfoButton } from "../../components/siteInfo/siteInfoButton/siteInfoButton"
import { formatSiteIdentity } from "./format-site-identity"
import { resolveSiteShellAccess } from "./resolve-site-shell-access"
import { SiteExplorerShell } from "./site-explorer-shell"
import { SiteInfoPanel } from "./site-info-panel"

export const SiteIndexRoute: React.FC = () => {
  const { t } = useTranslation("sites")
  const { buildSiteUrl } = useUrl()
  const { activeOrganization } = useActiveOrganization()
  const params = useParams()
  const matches = useMatches()
  const { siteData, siteIsFetched } = useGetSite(params.siteId ?? "")
  const { isOn: isSiteInfoOpen, toggle: toggleSiteInfo } = useToggle()

  const siteDetailRootMatch = useMatch(buildSiteUrl(SitePath.Site))
  const layoutMode = resolvePageLayoutMode(matches, "siteLayout")

  const access = resolveSiteShellAccess({
    activeOrganizationId: activeOrganization?.id,
    siteData,
    siteIsFetched,
  })

  if (siteDetailRootMatch) {
    return <Navigate replace={true} to={SitePath.Overview} />
  }

  if (access.status === "loading") {
    return null
  }

  const unknownSiteName = t("site.unknownSiteName")

  if (access.status === "unavailable") {
    return (
      <SiteExplorerShell heading={unknownSiteName} layoutMode={layoutMode}>
        <Page404 />
      </SiteExplorerShell>
    )
  }

  const outletContext: SiteLayoutContext = {
    orgId: activeOrganization?.id,
    site: access.site,
  }

  return (
    <>
      <SiteExplorerShell
        actions={
          <SiteInfoButton isOpen={isSiteInfoOpen} onClick={toggleSiteInfo} />
        }
        heading={formatSiteIdentity(access.site, unknownSiteName)}
        layoutMode={layoutMode}
      >
        <Outlet context={outletContext} />
      </SiteExplorerShell>
      <SiteInfoPanel
        isOpen={isSiteInfoOpen}
        onClose={toggleSiteInfo}
        site={access.site}
      />
    </>
  )
}
